// PRD.275 / PRD.276 — Elia AI provider.
//
// Shell-level host for Elia's conversation. Mounted once, above the routed
// shells, so closing/reopening the drawer never resets the in-memory thread.
// PRD.276 adds: a real backend client (behind the same EliaChatClient boundary),
// per-context in-memory threads keyed by general/patient/visit (never mixed),
// real page context, and cancellation of stale in-flight requests on context
// change (no false failure bubble after the context moves).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { isEliaAiEnabled } from './elia-feature-flag';
import { resolveEliaChatClient } from './elia-client-resolver';
import { isBusy, isSendableText, buildHistory } from './elia-chat-reducer';
import {
  createInitialThreadsState,
  eliaContextKey,
  eliaThreadsReducer,
} from './elia-threads-reducer';
import { ELIA_FAILURE_MESSAGE, createEliaId, createInitialThreadFor } from './elia-chat-fixtures';
import type {
  EliaChatClient,
  EliaContext,
  EliaContextSelector,
  EliaMessage,
} from './elia-chat-types';
import { useEliaEffectiveScope, type EliaScopeVisitOption } from './useEliaEffectiveScope';
import { scopeToEliaContext } from './elia-scope';
import { EliaAiDrawer } from './EliaAiDrawer';

export type EliaScopeControl = {
  availableVisits: EliaScopeVisitOption[];
  selectPatientScope: () => void;
  selectVisitScope: (visitId: string, visitDate?: string) => void;
};

export type EliaAiContextValue = {
  enabled: boolean;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  messages: EliaMessage[];
  isBusy: boolean;
  failedQuery: string | null;
  context: EliaContext;
  /** Active visit id when in visit scope (drives the Change selector highlight). */
  activeVisitId?: string;
  availableVisits: EliaScopeVisitOption[];
  sendMessage: (text: string) => void;
  retry: () => void;
  reset: () => void;
  selectPatientScope: () => void;
  selectVisitScope: (visitId: string, visitDate?: string) => void;
};

const EMPTY_VISITS: EliaScopeVisitOption[] = [];

const EliaAiContext = createContext<EliaAiContextValue | null>(null);

export function useEliaAi(): EliaAiContextValue {
  const ctx = useContext(EliaAiContext);
  if (!ctx) throw new Error('useEliaAi must be used within an <EliaAiProvider>');
  return ctx;
}

function selectorFrom(context: EliaContext): EliaContextSelector | undefined {
  if (context.visitId || context.patientId) {
    return {
      ...(context.patientId ? { patientId: context.patientId } : {}),
      ...(context.visitId ? { visitId: context.visitId } : {}),
    };
  }
  return undefined;
}

/**
 * Core provider. `context` is the active page context (supplied by the shell
 * connector below, or directly in tests). Client is injectable.
 */
export function EliaAiProvider({
  children,
  chatClient,
  context = {},
  scopeControl,
}: {
  children: ReactNode;
  chatClient?: EliaChatClient;
  context?: EliaContext;
  /** Manual Change control (supplied by the shell connector). */
  scopeControl?: EliaScopeControl;
}) {
  const enabled = isEliaAiEnabled(import.meta.env.VITE_ELIA_AI_ENABLED);
  const client = useMemo(() => chatClient ?? resolveEliaChatClient(), [chatClient]);

  const key = eliaContextKey(selectorFrom(context));

  const [threadsState, dispatch] = useReducer(
    eliaThreadsReducer,
    undefined,
    createInitialThreadsState,
  );

  // Active thread, with a synchronous fallback until ENSURE lands.
  const activeInitial = useMemo(() => createInitialThreadFor(context, key), [context, key]);
  const thread = threadsState.threads[key] ?? {
    messages: activeInitial,
    status: 'idle' as const,
    failedQuery: null,
  };

  // Latest snapshot for async handlers (avoids stale closures).
  const stateRef = useRef({ threadsState, key, context });
  stateRef.current = { threadsState, key, context };

  // Track the in-flight request so a context change can cancel it cleanly.
  const activeRequestRef = useRef<{
    req: { controller: AbortController; intentional: boolean };
    key: string;
  } | null>(null);

  // Tracks the effective-scope key to reset the conversation ONLY on a genuine
  // scope change (not on re-render or same-scope data refresh).
  const prevKeyRef = useRef(key);

  // On effective-scope change: abort any prior in-flight request WITHOUT
  // appending a failure bubble, then RESET the conversation for the new scope
  // (fresh scope-specific welcome). A same-key re-render just ensures the thread.
  useEffect(() => {
    const active = activeRequestRef.current;
    if (active && active.key !== key) {
      active.req.intentional = true;
      active.req.controller.abort();
      activeRequestRef.current = null;
    }
    const fresh = createInitialThreadFor(context, key);
    if (prevKeyRef.current !== key) {
      // Effective context key changed (patient↔visit, A→B) → reset conversation.
      dispatch({ type: 'THREAD', key, initial: fresh, action: { type: 'RESET', messages: fresh } });
      prevKeyRef.current = key;
    } else {
      dispatch({ type: 'ENSURE', key, initial: fresh });
    }
  }, [key, context]);

  const runQuery = useCallback(
    async (
      query: string,
      forKey: string,
      selector: EliaContextSelector | undefined,
      historyMessages: EliaMessage[],
      initialForKey: EliaMessage[],
    ) => {
      const req = { controller: new AbortController(), intentional: false };
      activeRequestRef.current = { req, key: forKey };
      try {
        const res = await client.sendMessage({
          query,
          history: buildHistory(historyMessages),
          context: selector,
          signal: req.controller.signal,
        });
        if (req.intentional) return; // context changed mid-flight — ignore
        dispatch({
          type: 'THREAD',
          key: forKey,
          initial: initialForKey,
          action: {
            type: 'SEND_SUCCESS',
            assistantMessage: {
              id: createEliaId('elia'),
              role: 'assistant',
              content: res.response,
              createdAt: new Date().toISOString(),
              status: 'sent',
              card: res.card,
              suggestions: res.suggestions,
            },
          },
        });
      } catch {
        if (req.intentional) return; // aborted by context switch — no false failure
        dispatch({
          type: 'THREAD',
          key: forKey,
          initial: initialForKey,
          action: {
            type: 'SEND_FAILURE',
            query,
            failureMessage: {
              id: createEliaId('elia-fail'),
              role: 'assistant',
              content: ELIA_FAILURE_MESSAGE,
              createdAt: new Date().toISOString(),
              status: 'failed',
            },
          },
        });
      } finally {
        if (activeRequestRef.current?.req === req) activeRequestRef.current = null;
      }
    },
    [client],
  );

  const sendMessage = useCallback(
    (rawText: string) => {
      const text = rawText.trim();
      if (!isSendableText(text)) return;
      const { threadsState: ts, key: k, context: ctx } = stateRef.current;
      const existing = ts.threads[k];
      if (existing && isBusy(existing)) return; // block duplicate while pending
      const initialForKey = createInitialThreadFor(ctx, k);
      const base = existing?.messages ?? initialForKey;
      const userMessage: EliaMessage = {
        id: createEliaId('user'),
        role: 'user',
        content: text,
        createdAt: new Date().toISOString(),
        status: 'sent',
      };
      dispatch({
        type: 'THREAD',
        key: k,
        initial: initialForKey,
        action: { type: 'SEND_START', userMessage },
      });
      void runQuery(text, k, selectorFrom(ctx), [...base, userMessage], initialForKey);
    },
    [runQuery],
  );

  const retry = useCallback(() => {
    const { threadsState: ts, key: k, context: ctx } = stateRef.current;
    const existing = ts.threads[k];
    const query = existing?.failedQuery;
    if (!query) return;
    if (existing && isBusy(existing)) return;
    const initialForKey = createInitialThreadFor(ctx, k);
    dispatch({ type: 'THREAD', key: k, initial: initialForKey, action: { type: 'RETRY_START' } });
    const history = (existing?.messages ?? initialForKey).filter((m) => m.status !== 'failed');
    void runQuery(query, k, selectorFrom(ctx), history, initialForKey);
  }, [runQuery]);

  const reset = useCallback(() => {
    const { key: k, context: ctx } = stateRef.current;
    const fresh = createInitialThreadFor(ctx, k);
    dispatch({
      type: 'THREAD',
      key: k,
      initial: fresh,
      action: { type: 'RESET', messages: fresh },
    });
  }, []);

  // ── Open/close with focus return ──
  const [isOpen, setIsOpen] = useState(false);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  const open = useCallback(() => {
    lastFocusedRef.current = (document.activeElement as HTMLElement | null) ?? null;
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
    const el = lastFocusedRef.current;
    if (el && typeof el.focus === 'function') requestAnimationFrame(() => el.focus());
  }, []);
  const toggle = useCallback(() => {
    if (isOpen) close();
    else open();
  }, [isOpen, open, close]);

  const availableVisits = scopeControl?.availableVisits ?? EMPTY_VISITS;
  const selectPatientScope = useCallback(() => {
    scopeControl?.selectPatientScope();
  }, [scopeControl]);
  const selectVisitScope = useCallback(
    (visitId: string, visitDate?: string) => {
      scopeControl?.selectVisitScope(visitId, visitDate);
    },
    [scopeControl],
  );

  const value = useMemo<EliaAiContextValue>(
    () => ({
      enabled,
      isOpen,
      open,
      close,
      toggle,
      messages: thread.messages,
      isBusy: thread.status === 'sending',
      failedQuery: thread.failedQuery,
      context,
      activeVisitId: context.visitId,
      availableVisits,
      sendMessage,
      retry,
      reset,
      selectPatientScope,
      selectVisitScope,
    }),
    [
      enabled,
      isOpen,
      open,
      close,
      toggle,
      thread,
      context,
      availableVisits,
      sendMessage,
      retry,
      reset,
      selectPatientScope,
      selectVisitScope,
    ],
  );

  return (
    <EliaAiContext.Provider value={value}>
      {children}
      {enabled ? <EliaAiDrawer /> : null}
    </EliaAiContext.Provider>
  );
}

/**
 * Shell connector: supplies the real active patient/visit context to the core
 * provider. This is what App mounts. Kept separate so EliaAiProvider stays
 * unit-testable without the patient-workspace context.
 */
export function EliaAiShellProvider({ children }: { children: ReactNode }) {
  const { scope, patientName, availableVisits, setManualScope } = useEliaEffectiveScope();
  const context = useMemo(() => scopeToEliaContext(scope, patientName), [scope, patientName]);

  const scopeControl = useMemo<EliaScopeControl>(
    () => ({
      availableVisits,
      selectPatientScope: () => {
        if (scope?.patientId) setManualScope({ kind: 'patient', patientId: scope.patientId });
      },
      selectVisitScope: (visitId, visitDate) => {
        if (scope?.patientId) {
          setManualScope({ kind: 'visit', patientId: scope.patientId, visitId, visitDate });
        }
      },
    }),
    [availableVisits, scope?.patientId, setManualScope],
  );

  return (
    <EliaAiProvider context={context} scopeControl={scopeControl}>
      {children}
    </EliaAiProvider>
  );
}
